import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";

import { actions as AuthActions } from "../store/authRedux/actions";
import { ProjectApi } from "../services";

export const ACTIVE_TRACKING_KEY = "[Active] Time Tracking";

/**
 * How long a tab may keep believing its own copy of the running timer before it
 * asks the server again. The timer can be started or stopped from another tab,
 * another device or the mobile app, and until now only a full page reload
 * brought this one back in sync.
 */
const SYNC_INTERVAL_MS = 60 * 1000;

/**
 * Set whenever this tab starts or stops a timer itself. A poll that left before
 * that write must not land after it and undo it — the server response it is
 * carrying is older than what we already know.
 */
let lastLocalWrite = 0;

const normalize = (record) => (record && !record.to ? record : null);

/**
 * The single reader/writer for "which task am I timing right now".
 *
 * Every consumer used to re-derive this from `auth.user.current_time_tracking`
 * with its own truthiness check, which is why the top bar kept counting after a
 * task stopped its own timer: the task wrote the *finished* record back to the
 * store and the top bar only checked that something was there.
 */
export const useTimeTracking = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const activeTracking = useSelector(({ auth }) =>
    normalize(auth.user?.current_time_tracking),
  );

  const setTracking = useCallback(
    (record) => {
      lastLocalWrite = Date.now();
      dispatch(AuthActions.setCurrentTracking(normalize(record)));
      queryClient.invalidateQueries({ queryKey: [ACTIVE_TRACKING_KEY] });
    },
    [dispatch, queryClient],
  );

  const clearTracking = useCallback(() => setTracking(null), [setTracking]);

  /** Ask the server now instead of waiting for the next poll. */
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [ACTIVE_TRACKING_KEY] }),
    [queryClient],
  );

  /** Ids come back as numbers from the API and as strings from the URL. */
  const isTrackingTask = useCallback(
    (taskId) =>
      !!activeTracking &&
      taskId != null &&
      String(activeTracking.task_id) === String(taskId),
    [activeTracking],
  );

  return { activeTracking, isTrackingTask, setTracking, clearTracking, refresh };
};

/**
 * Mounted once, by the layout. Keeps the store's copy of the running timer
 * honest without a reload, and stays deliberately quiet: if the endpoint is not
 * reachable the app simply keeps the state it already has.
 */
export const useTrackingSync = (enabled = true) => {
  const dispatch = useDispatch();

  useQuery({
    queryKey: [ACTIVE_TRACKING_KEY],
    queryFn: async () => {
      const startedAt = Date.now();

      try {
        const result = await ProjectApi.getCurrentRecordTime();
        const record = normalize(result.data?.time_tracking_record);

        if (startedAt >= lastLocalWrite) {
          dispatch(AuthActions.setCurrentTracking(record));
        }

        return record;
      } catch (e) {
        return null;
      }
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: SYNC_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
};
